import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { House, MagnifyingGlass, DotsThree, Eye, Trash, MapPin, Ruler, Bed, Pencil, Plus, FunnelSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { PropertyEditDialog } from '../components/PropertyEditDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_OPTIONS = [
    { value: 'all', label: 'Alle Typen' },
    { value: 'apartment', label: 'Wohnung' },
    { value: 'house', label: 'Haus' },
    { value: 'land', label: 'Grundstück' },
    { value: 'office', label: 'Büro' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'warehouse', label: 'Lager/Halle' },
    { value: 'other', label: 'Sonstige' },
];

const MARKETING_TYPE_OPTIONS = [
    { value: 'all', label: 'Alle' },
    { value: 'sale', label: 'Kauf' },
    { value: 'rent', label: 'Miete' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'Alle Status' },
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'sold', label: 'Verkauft' },
    { value: 'rented', label: 'Vermietet' },
];

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnung',
    house: 'Haus',
    land: 'Grundstück',
    office: 'Büro',
    retail: 'Einzelhandel',
    warehouse: 'Lager/Halle',
    other: 'Sonstige'
};

const ENERGY_CLASS_OPTIONS = [
    { value: 'all', label: 'Alle Klassen' },
    { value: 'A+', label: 'A+' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'F', label: 'F' },
    { value: 'G', label: 'G' },
    { value: 'H', label: 'H' },
];

const ITEMS_PER_PAGE = 10;

export default function PropertiesPage() {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [deleteId, setDeleteId] = useState(null);
    const [editProperty, setEditProperty] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    
    // Filters
    const [search, setSearch] = useState('');
    const [propertyType, setPropertyType] = useState('all');
    const [marketingType, setMarketingType] = useState('all');
    const [status, setStatus] = useState('all');
    const [yearBuiltMin, setYearBuiltMin] = useState('');
    const [yearBuiltMax, setYearBuiltMax] = useState('');
    const [floorMin, setFloorMin] = useState('');
    const [floorMax, setFloorMax] = useState('');
    const [energyClass, setEnergyClass] = useState('all');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (propertyType !== 'all') params.append('property_type', propertyType);
            if (marketingType !== 'all') params.append('marketing_type', marketingType);
            if (status !== 'all') params.append('status', status);
            if (yearBuiltMin) params.append('year_built_min', yearBuiltMin);
            if (yearBuiltMax) params.append('year_built_max', yearBuiltMax);
            if (floorMin) params.append('floor_min', floorMin);
            if (floorMax) params.append('floor_max', floorMax);
            if (energyClass !== 'all') params.append('energy_efficiency_class', energyClass);
            params.append('limit', ITEMS_PER_PAGE.toString());
            params.append('skip', ((currentPage - 1) * ITEMS_PER_PAGE).toString());
            
            const { data } = await axios.get(`${API}/api/properties?${params.toString()}`, { withCredentials: true });
            setProperties(data.properties);
            setTotal(data.total);
        } catch (error) {
            console.error('Error fetching properties:', error);
            toast.error('Fehler beim Laden der Immobilien');
        } finally {
            setLoading(false);
        }
    }, [search, propertyType, marketingType, status, yearBuiltMin, yearBuiltMax, floorMin, floorMax, energyClass, currentPage]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchProperties();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchProperties]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, propertyType, marketingType, status, yearBuiltMin, yearBuiltMax, floorMin, floorMax, energyClass]);

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API}/api/properties/${deleteId}`, { withCredentials: true });
            toast.success('Immobilie gelöscht');
            fetchProperties();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
        setDeleteId(null);
    };

    const handleEdit = (property) => {
        setEditProperty(property);
        setEditDialogOpen(true);
    };

    const handleCreate = () => {
        setEditProperty(null);
        setEditDialogOpen(true);
    };

    const formatPrice = (price, currency = 'EUR') => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0
        }).format(price);
    };

    return (
        <div className="space-y-6" data-testid="properties-page">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    Immobilien
                </h1>
                <div className="flex gap-2">
                    <Button className="rounded-sm" onClick={handleCreate} data-testid="create-property-btn">
                        <Plus size={16} className="mr-2" />
                        Neu
                    </Button>
                    <Link to="/import">
                        <Button variant="outline" className="rounded-sm" data-testid="import-btn">
                            <House size={16} className="mr-2" />
                            Import
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Filters */}
            <Card className="border" data-testid="filters-card">
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="relative">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Titel, Stadt oder Objekt-Nr. suchen..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={propertyType} onValueChange={setPropertyType}>
                            <SelectTrigger className="rounded-sm" data-testid="property-type-filter">
                                <SelectValue placeholder="Immobilientyp" />
                            </SelectTrigger>
                            <SelectContent>
                                {PROPERTY_TYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={marketingType} onValueChange={setMarketingType}>
                            <SelectTrigger className="rounded-sm" data-testid="marketing-type-filter">
                                <SelectValue placeholder="Vermarktung" />
                            </SelectTrigger>
                            <SelectContent>
                                {MARKETING_TYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="rounded-sm flex-1" data-testid="status-filter">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUS_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button 
                                variant={showAdvanced ? "default" : "outline"} 
                                size="icon" 
                                className="rounded-sm shrink-0"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                data-testid="toggle-advanced-filters"
                            >
                                <FunnelSimple size={16} />
                            </Button>
                        </div>
                    </div>
                    
                    {/* Advanced Filters */}
                    {showAdvanced && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t" data-testid="advanced-filters">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Baujahr</label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        placeholder="Von"
                                        value={yearBuiltMin}
                                        onChange={(e) => setYearBuiltMin(e.target.value)}
                                        className="rounded-sm"
                                        data-testid="year-built-min"
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Bis"
                                        value={yearBuiltMax}
                                        onChange={(e) => setYearBuiltMax(e.target.value)}
                                        className="rounded-sm"
                                        data-testid="year-built-max"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Etage</label>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        placeholder="Von"
                                        value={floorMin}
                                        onChange={(e) => setFloorMin(e.target.value)}
                                        className="rounded-sm"
                                        data-testid="floor-min"
                                    />
                                    <Input
                                        type="number"
                                        placeholder="Bis"
                                        value={floorMax}
                                        onChange={(e) => setFloorMax(e.target.value)}
                                        className="rounded-sm"
                                        data-testid="floor-max"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Energieeffizienzklasse</label>
                                <Select value={energyClass} onValueChange={setEnergyClass}>
                                    <SelectTrigger className="rounded-sm" data-testid="energy-class-filter">
                                        <SelectValue placeholder="Energieklasse" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ENERGY_CLASS_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Results Count */}
            <div className="text-sm text-muted-foreground">
                {total} Immobilie{total !== 1 ? 'n' : ''} gefunden
            </div>

            {/* Properties Table */}
            <Card className="border" data-testid="properties-table">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F8F9FA]">
                            <TableHead className="font-semibold">Obj.-Nr.</TableHead>
                            <TableHead className="font-semibold">Titel</TableHead>
                            <TableHead className="font-semibold">Typ</TableHead>
                            <TableHead className="font-semibold">Ort</TableHead>
                            <TableHead className="font-semibold">Fläche</TableHead>
                            <TableHead className="font-semibold">Zimmer</TableHead>
                            <TableHead className="font-semibold">Preis</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <TableRow key={i}>
                                    {[...Array(9)].map((_, j) => (
                                        <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : properties.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                                    Keine Immobilien gefunden
                                </TableCell>
                            </TableRow>
                        ) : (
                            properties.map((prop) => (
                                <TableRow key={prop.id} className="table-row-hover" data-testid={`property-row-${prop.id}`}>
                                    <TableCell className="text-xs text-muted-foreground font-mono">
                                        {prop.object_nr_external || prop.external_id || '-'}
                                    </TableCell>
                                    <TableCell className="font-medium max-w-[200px] truncate">
                                        <div className="flex items-center gap-1.5">
                                            <span className="truncate">{prop.title}</span>
                                            {prop.is_investment && (
                                                <Badge
                                                    className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100 shrink-0"
                                                    data-testid={`investment-badge-${prop.id}`}
                                                >
                                                    Anlage
                                                </Badge>
                                            )}
                                            {(prop.is_international || (prop.address?.country && !['DE','D','Deutschland','deutschland','Germany','germany'].includes(prop.address.country))) && (
                                                <Badge
                                                    className="text-[10px] px-1.5 py-0 bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-100 shrink-0"
                                                    data-testid={`international-badge-${prop.id}`}
                                                >
                                                    Ausland
                                                </Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="rounded-sm">
                                            {PROPERTY_TYPE_LABELS[prop.property_type] || prop.property_type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1 text-muted-foreground">
                                            <MapPin size={14} />
                                            <span>{prop.address?.city || '-'}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {prop.area ? (
                                            <div className="flex items-center gap-1">
                                                <Ruler size={14} className="text-muted-foreground" />
                                                <span>{prop.area} m²</span>
                                            </div>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {prop.rooms ? (
                                            <div className="flex items-center gap-1">
                                                <Bed size={14} className="text-muted-foreground" />
                                                <span>{prop.rooms}</span>
                                            </div>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {formatPrice(prop.price, prop.currency)}
                                        {prop.marketing_type === 'rent' && <span className="text-muted-foreground text-xs">/Monat</span>}
                                    </TableCell>
                                    <TableCell>
                                        <Badge 
                                            variant={prop.status === 'active' ? 'default' : 'secondary'}
                                            className="rounded-sm capitalize"
                                        >
                                            {prop.status === 'active' ? 'Aktiv' : prop.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`property-menu-${prop.id}`}>
                                                    <DotsThree size={16} weight="bold" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem asChild>
                                                    <Link to={`/properties/${prop.id}`} className="cursor-pointer">
                                                        <Eye size={14} className="mr-2" />
                                                        Anzeigen
                                                    </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    className="cursor-pointer"
                                                    onClick={() => handleEdit(prop)}
                                                >
                                                    <Pencil size={14} className="mr-2" />
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem 
                                                    className="text-destructive cursor-pointer"
                                                    onClick={() => setDeleteId(prop.id)}
                                                >
                                                    <Trash size={14} className="mr-2" />
                                                    Löschen
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={total}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            )}

            {/* Edit Dialog */}
            <PropertyEditDialog
                property={editProperty}
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                onSaved={fetchProperties}
            />

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Immobilie löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Diese Aktion kann nicht rückgängig gemacht werden. Die Immobilie wird dauerhaft gelöscht.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
